import { useCallback, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useTranslate } from '@tolgee/react';

import { useToast } from '~/app/contexts/ToastContext';
import { PRINT_CONTAINER_ID } from '~/shared/lib/html2pdf';

import { useHandleStartKeepDoingContainers } from '../useHandleStartKeepDoingContainers';

import {
  calculatePageHeight,
  createStyledClone,
  handleShowHideElements,
} from './domUtils';
import { generatePdf } from './pdfGenerator';
import { createStylesheetManager } from './stylesheetManager';
import { EmployeeInfo, PrintContextState } from './types';

export function usePrint(): PrintContextState {
  const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const [isPrinting, setIsPrinting] = useState(false);
  const { t } = useTranslate('results');

  const handleSetPdfEmployeeInfo = useCallback((info: EmployeeInfo | null) => {
    setEmployeeInfo(info);
  }, []);

  const { splitCommentCards } = useHandleStartKeepDoingContainers();

  const buildPDfName = useCallback(() => {
    if (!employeeInfo) return 'document.pdf';

    const label = '180-360';

    return `${employeeInfo.year}_${label}_${employeeInfo.name}_${employeeInfo.globalId}.pdf`;
  }, [employeeInfo]);

  const prepareDocumentForPrinting = useCallback(
    (source: HTMLElement) => {
      const styledCloneContainer = createStyledClone(source);
      const styledClone = styledCloneContainer.firstElementChild as HTMLElement;

      handleShowHideElements(styledClone);

      const cardContainers = styledClone.querySelectorAll(
        '.print-card-container'
      );
      cardContainers.forEach((container, index) => {
        const pageHeight = calculatePageHeight(index);
        splitCommentCards(container as HTMLElement, pageHeight);
      });

      return { styledCloneContainer, styledClone };
    },
    [splitCommentCards]
  );

  const reactToPrintFn = useReactToPrint({
    contentRef,
    documentTitle: buildPDfName(),
    async print() {
      const stylesheetManager = createStylesheetManager();

      const { restore: restoreTailwindStyles } =
        stylesheetManager.disableTailwindStyles();

      let styledCloneContainer: HTMLElement | null = null;

      try {
        const original = document.getElementById(PRINT_CONTAINER_ID);

        if (!original) return;

        const prepared = prepareDocumentForPrinting(original);
        styledCloneContainer = prepared.styledCloneContainer;

        await generatePdf(prepared.styledClone, buildPDfName());
      } catch (error) {
        console.error(error);

        toast.add({
          type: 'error',
          description: t('results.pdf.error_to_generate'),
        });
      } finally {
        restoreTailwindStyles();

        if (styledCloneContainer) {
          document.body.removeChild(styledCloneContainer);
        }

        setIsPrinting(false);
      }
    },
  });

  const onPrint = useCallback(() => {
    setIsPrinting(true);
    setTimeout(() => reactToPrintFn(), 100);
  }, [reactToPrintFn]);

  return useMemo(
    () => ({
      isPrinting,
      onPrint,
      setIsPrinting,
      handleSetPdfEmployeeInfo,
      contentRef,
    }),
    [isPrinting, onPrint, handleSetPdfEmployeeInfo]
  );
}
